import React, { useCallback, useEffect, useState } from "react";
import { Trash2, Loader2, RefreshCw, ShieldAlert, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { formatApiErrorDetail } from "@/lib/apiError";

/**
 * Compact orphan-files cleanup card.
 *
 * Lives at the bottom of the admin dashboard. Detects DB records whose
 * underlying file is unreachable (neither on local disk nor in Object
 * Storage) and lets the admin prune them in one click. Useful after a
 * migration like the local-disk → Object Storage one we just did.
 */
const OrphanCleanupCard = () => {
  const { authedAxios } = useAuth();
  const { lang } = useLanguage();
  const [scanning, setScanning] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [orphans, setOrphans] = useState(null); // null = not scanned yet, [] = clean

  const scan = useCallback(async () => {
    setScanning(true);
    try {
      const ax = authedAxios();
      const res = await ax.get("/admin/orphan-files/scan");
      setOrphans(res.data?.orphans || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err));
    } finally {
      setScanning(false);
    }
  }, [authedAxios]);

  // Initial scan on mount so the admin sees the badge count immediately.
  useEffect(() => {
    scan();
  }, [scan]);

  const cleanup = async () => {
    if (!Array.isArray(orphans) || orphans.length === 0) return;
    const confirmed = window.confirm(
      lang === "fr"
        ? `Supprimer définitivement ${orphans.length} enregistrement(s) orphelin(s) ?`
        : `Permanently delete ${orphans.length} orphan record(s)?`,
    );
    if (!confirmed) return;
    setCleaning(true);
    try {
      const ax = authedAxios();
      const res = await ax.post("/admin/orphan-files/cleanup");
      const total = Object.values(res.data?.deleted || {}).reduce(
        (a, b) => a + b,
        0,
      );
      toast.success(
        lang === "fr"
          ? `${total} enregistrement(s) supprimé(s)`
          : `${total} record(s) deleted`,
      );
      await scan();
    } catch (err) {
      toast.error(formatApiErrorDetail(err));
    } finally {
      setCleaning(false);
    }
  };

  const count = Array.isArray(orphans) ? orphans.length : null;

  return (
    <section
      data-testid="admin-orphan-cleanup-card"
      className="mt-12 border border-[#F2EBE5]/10 bg-[#0a0a0a]"
    >
      <div className="px-6 py-5 border-b border-[#F2EBE5]/5 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          {count === 0 ? (
            <CheckCircle2 className="w-5 h-5 text-emerald-400" strokeWidth={1.5} />
          ) : (
            <ShieldAlert className="w-5 h-5 text-[#C5A059]" strokeWidth={1.5} />
          )}
          <div>
            <h3 className="font-serif-display text-xl text-[#F2EBE5]">
              {lang === "fr" ? "Maintenance des fichiers" : "File maintenance"}
            </h3>
            <p className="text-xs text-[#A19E9C] mt-0.5">
              {count === null
                ? lang === "fr"
                  ? "Analyse en cours…"
                  : "Scanning…"
                : count === 0
                  ? lang === "fr"
                    ? "Aucun fichier orphelin détecté."
                    : "No orphan records found."
                  : lang === "fr"
                    ? `${count} enregistrement(s) pointent vers un fichier introuvable.`
                    : `${count} record(s) point to a missing file.`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={scan}
            disabled={scanning}
            data-testid="orphan-rescan-btn"
            className="inline-flex items-center gap-2 px-3 py-2 border border-[#F2EBE5]/15 hover:border-[#C5A059] text-[10px] uppercase tracking-[0.25em] text-[#F2EBE5] hover:text-[#C5A059] transition-colors disabled:opacity-50"
          >
            {scanning ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={1.5} />
            ) : (
              <RefreshCw className="w-3.5 h-3.5" strokeWidth={1.5} />
            )}
            {lang === "fr" ? "Rescanner" : "Re-scan"}
          </button>
          <button
            type="button"
            onClick={cleanup}
            disabled={!count || cleaning || scanning}
            data-testid="orphan-cleanup-btn"
            className="inline-flex items-center gap-2 px-4 py-2 border border-[#C5A059] bg-[#C5A059]/10 hover:bg-[#C5A059]/20 text-[10px] uppercase tracking-[0.25em] text-[#C5A059] transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          >
            {cleaning ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={1.5} />
            ) : (
              <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
            )}
            {lang === "fr"
              ? `Nettoyer${count ? ` (${count})` : ""}`
              : `Cleanup${count ? ` (${count})` : ""}`}
          </button>
        </div>
      </div>

      {Array.isArray(orphans) && orphans.length > 0 && (
        <ul
          className="divide-y divide-[#F2EBE5]/5 max-h-60 overflow-y-auto"
          data-testid="orphan-list"
        >
          {orphans.slice(0, 50).map((o, i) => (
            <li
              key={`${o.collection}-${o.id}-${i}`}
              className="px-6 py-3 flex items-center justify-between gap-4 text-xs"
            >
              <div className="flex-1 min-w-0">
                <div className="text-[#F2EBE5] truncate">
                  {o.url || <em className="text-[#A19E9C]">— (no url)</em>}
                </div>
                <div className="text-[10px] text-[#A19E9C] mt-0.5 flex items-center gap-3">
                  <span>{o.collection.replace("site_content.", "")}</span>
                  {o.service_slug && (
                    <>
                      <span className="text-[#F2EBE5]/20">•</span>
                      <span>{o.service_slug}</span>
                    </>
                  )}
                  {o.is_cover && (
                    <>
                      <span className="text-[#F2EBE5]/20">•</span>
                      <span className="text-[#C5A059]">cover</span>
                    </>
                  )}
                </div>
              </div>
            </li>
          ))}
          {orphans.length > 50 && (
            <li className="px-6 py-3 text-[10px] uppercase tracking-[0.2em] text-[#A19E9C] text-center">
              {lang === "fr"
                ? `+ ${orphans.length - 50} autres…`
                : `+ ${orphans.length - 50} more…`}
            </li>
          )}
        </ul>
      )}
    </section>
  );
};

export default OrphanCleanupCard;
